import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useAppConfig } from "../hooks/useAppConfig";
import { useStats } from "../hooks/useStats";
import { ApiError } from "../lib/api";
import { Button } from "../components/Button";
import { formatBytes } from "../lib/format";

export function SettingsPage() {
  const { username, changePassword } = useAuth();
  const toast = useToast();
  const config = useAppConfig();
  const { stats } = useStats(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated. Other sessions were signed out.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scrollable h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold tracking-tight text-zinc-900">Settings</h1>
          <Link
            to="/app"
            className="text-[13px] font-medium text-zinc-600 underline decoration-zinc-300 underline-offset-2 transition-colors hover:text-zinc-900"
          >
            Back to library
          </Link>
        </div>

        <section className="glass rounded-2xl p-6 shadow-glass">
          <h2 className="text-sm font-semibold text-zinc-900">Account</h2>
          <p className="mt-1 text-[13px] leading-relaxed text-zinc-600">
            Signed in as <span className="font-medium text-zinc-800">{username}</span>. Changing the password signs out
            every other session.
          </p>
          <form onSubmit={onSubmit} className="mt-4 space-y-3">
            <input
              type="password"
              placeholder="Current password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="field"
              required
            />
            <input
              type="password"
              placeholder="New password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              className="field"
              required
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="field"
              required
            />
            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50/80 px-3 py-2 text-[13px] text-red-700">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" loading={busy}>
              Update password
            </Button>
          </form>
        </section>

        <section className="glass rounded-2xl p-6 shadow-glass">
          <h2 className="text-sm font-semibold text-zinc-900">Library</h2>
          {stats ? (
            <dl className="mt-3 divide-y divide-zinc-900/10 text-[13px]">
              <Row label="Images" value={stats.images.toLocaleString()} />
              <Row label="Folders" value={stats.folders.toLocaleString()} />
              <Row label="Total size" value={formatBytes(stats.totalSizeBytes)} />
            </dl>
          ) : (
            <p className="mt-2 text-[13px] text-zinc-500">Loading</p>
          )}
        </section>

        <section className="glass rounded-2xl p-6 shadow-glass">
          <h2 className="text-sm font-semibold text-zinc-900">Storage and delivery</h2>
          {config ? (
            <dl className="mt-3 divide-y divide-zinc-900/10 text-[13px]">
              <Row label="Image base URL" value={config.publicAssetsBaseUrl} mono />
              <Row label="Maximum file size" value={formatBytes(config.maxImageBytes)} />
              <Row label="SVG uploads" value={config.allowSvg ? "Allowed" : "Disabled"} />
              <Row label="Storage driver" value={config.driver} mono />
            </dl>
          ) : (
            <p className="mt-2 text-[13px] text-zinc-500">Loading</p>
          )}
        </section>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt className="flex-shrink-0 text-zinc-500">{label}</dt>
      <dd className={`min-w-0 break-all font-medium text-zinc-800 sm:text-right ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
